export function emptyIntervention() {
  return { forward: false, backward: false, both: false, reverse: false, factor: 1, newCap: null };
}

export function booleanFlag(value, fallback = false) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') {
    return ['true', '1', 'yes'].includes(value.trim().toLowerCase());
  }
  return value === true || value === 1;
}

export function isOneWayEdge(meta = {}, properties = {}) {
  const oppositeUid = meta.opposite_uid || properties.opposite_uid;
  const raw = meta.oneway ?? properties.oneway;
  return booleanFlag(raw, !oppositeUid);
}

export function ensureIntervention(map, uid) {
  if (!map[uid]) map[uid] = emptyIntervention();
  return map[uid];
}

export function cloneInterventions(src) {
  return Object.fromEntries(Object.entries(src || {}).map(([k, v]) => [k, { ...emptyIntervention(), ...v }]));
}

export function isActiveIntervention(a, baseCap) {
  if (!a) return false;
  const factor = Number(a.factor ?? 1);
  return !!(
    a.forward || a.backward || a.both || a.reverse ||
    (a.newCap != null && Number(a.newCap) !== Number(baseCap)) ||
    (factor && factor !== 1)
  );
}

export function edgeClosed(a) {
  return !!(a && (a.forward || a.backward || a.both));
}

export function effectiveCapacity(baseCap, a) {
  const base = a?.newCap != null ? Number(a.newCap) : Number(baseCap || 1);
  const factor = Number(a?.factor ?? 1) || 1;
  return Math.max(1, Math.round(base * factor));
}

export function summaryAction(meta, a) {
  const tags = [];
  if (!a) return tags;
  if (a.both) tags.push('chiusura totale');
  else if (a.forward) tags.push(meta?.oneway ? 'strada chiusa' : 'chiusura dir. principale');
  if (a.backward && !meta?.oneway) tags.push('chiusura dir. opposta');
  if (a.reverse) tags.push('senso invertito');
  if (Number(a.factor) !== 1) tags.push(`capacità ×${a.factor}`);
  if (a.newCap != null && Number(a.newCap) !== Number(meta?.capacity_vph)) tags.push(`capacità ${a.newCap} v/h`);
  return tags;
}

function streetName(uid, featuresByUid) {
  const feature = featuresByUid?.get?.(uid);
  const raw = feature?.properties?.name;
  const name = Array.isArray(raw) ? raw[0] : raw;
  return String(name || 'strada senza nome').trim();
}

export function formatScenarioActions(interventions, newRoads, edgeMeta = {}, featuresByUid) {
  const actions = [];
  Object.entries(interventions || {}).forEach(([uid, intervention]) => {
    const meta = edgeMeta[uid] || {};
    if (!isActiveIntervention(intervention, meta.capacity_vph)) return;
    const name = streetName(uid, featuresByUid);
    if (intervention.both) actions.push(`chiusura entrambe le direzioni ${name}`);
    else {
      if (intervention.forward) {
        actions.push(`${isOneWayEdge(meta) ? 'chiusura' : 'chiusura direzione principale'} ${name}`);
      }
      if (intervention.backward) actions.push(`chiusura direzione opposta ${name}`);
    }
    if (intervention.reverse) actions.push(`cambio senso di marcia ${name}`);
    if (Number(intervention.factor ?? 1) !== 1) {
      actions.push(`capacità ${name} modificata di un fattore ${intervention.factor}`);
    }
    if (
      intervention.newCap != null
      && Number(intervention.newCap) !== Number(meta.capacity_vph)
    ) {
      actions.push(`capacità ${name} impostata a ${intervention.newCap} veh/h`);
    }
  });
  (newRoads || []).forEach((road) => {
    actions.push(`creazione nuova strada "${road.name || 'Nuova strada'}"`);
  });
  return actions;
}

function canonicalValue(value) {
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, canonicalValue(value[key])]),
  );
}

export function scenarioDefinitionFingerprint(definition) {
  return JSON.stringify(canonicalValue(definition || {}));
}

export function focusedEdgeOpacity(uid, focusedUids, normalOpacity = 1) {
  return focusedUids?.size && !focusedUids.has(uid) ? normalOpacity * 0.12 : normalOpacity;
}

export function closedEdgeUids(interventions, edgeMeta = {}) {
  const uids = new Set();
  Object.entries(interventions || {}).forEach(([uid, a]) => {
    if (a.both || a.forward) uids.add(uid);
    if (a.backward || a.both) {
      const opp = edgeMeta[uid]?.opposite_uid;
      if (opp) uids.add(opp);
    }
  });
  return uids;
}

export function replaceClosedEdgeUids(interventions, closedUids) {
  const next = cloneInterventions(interventions || {});
  Object.keys(next).forEach((uid) => {
    next[uid] = {
      ...next[uid],
      forward: false,
      backward: false,
      both: false,
    };
  });
  (closedUids || new Set()).forEach((uid) => {
    next[uid] = {
      ...emptyIntervention(),
      ...(next[uid] || {}),
      forward: true,
    };
  });
  return next;
}

export function directionalClosureState(interventions, uid, edgeMeta = {}) {
  const oppositeUid = edgeMeta[uid]?.opposite_uid || null;
  const closed = closedEdgeUids(interventions, edgeMeta);
  return {
    oppositeUid,
    forward: closed.has(uid),
    backward: !!oppositeUid && closed.has(oppositeUid),
  };
}

export function applyDirectionalClosure(interventions, uid, edgeMeta, draft) {
  const next = cloneInterventions(interventions || {});
  const oppositeUid = edgeMeta[uid]?.opposite_uid || null;
  const clear = (edgeUid) => {
    if (!edgeUid || !next[edgeUid]) return;
    next[edgeUid] = {
      ...next[edgeUid],
      forward: false,
      backward: false,
      both: false,
    };
  };
  clear(uid);
  clear(oppositeUid);

  const forward = !!(draft.forward || draft.both);
  const backward = !!oppositeUid && !!(draft.backward || draft.both);
  next[uid] = {
    ...emptyIntervention(),
    ...(next[uid] || {}),
    ...draft,
    forward: forward && !backward,
    backward: backward && !forward,
    both: forward && backward,
  };
  return next;
}

export function toApiScenario(interventions, newRoads, edgeMeta = {}) {
  const payload = {};
  Object.entries(interventions || {}).forEach(([uid, a]) => {
    if (!isActiveIntervention(a, edgeMeta[uid]?.capacity_vph)) return;
    payload[uid] = {
      forward_closed: !!(a.forward || a.both),
      backward_closed: !!(a.backward || a.both),
      both_closed: !!a.both,
      reversed: !!a.reverse,
      capacity_vph: a.newCap != null ? Number(a.newCap) : null,
      capacity_factor: Number(a.factor ?? 1) || 1,
    };
  });
  // A full or opposite closure must name the reverse edge itself. The
  // assignment removes the edge whose id is marked forward_closed.
  closedEdgeUids(interventions, edgeMeta).forEach((uid) => {
    payload[uid] = {
      forward_closed: false,
      backward_closed: false,
      both_closed: false,
      reversed: false,
      capacity_vph: null,
      capacity_factor: 1,
      ...(payload[uid] || {}),
      forward_closed: true,
    };
  });
  return {
    interventions: payload,
    new_roads: (newRoads || []).map((r) => ({
      temp_id: r.temp_id || r.uid,
      origin_zone_id: r.origin,
      destination_zone_id: r.destination,
      graph_u: r.graph_u || r.origin,
      graph_v: r.graph_v || r.destination,
      geometry: r.geometry,
      lanes: Number(r.lanes) || 1,
      speed_kph: Number(r.speed) || 30,
      capacity_vph: Number(r.capacity) || undefined,
      capacity_factor: Number(r.factor) || 1,
      two_way: !!(r.twoWay ?? r.two_way),
      highway: r.highway || 'residential',
      name: r.name,
    })),
  };
}

export function fromApiInterventions(definition = {}) {
  return Object.fromEntries(
    Object.entries(definition.interventions || {}).map(([uid, intervention]) => [
      uid,
      {
        forward: !!intervention.forward_closed,
        backward: !!intervention.backward_closed,
        both: !!intervention.both_closed,
        reverse: !!intervention.reversed,
        factor: intervention.capacity_factor ?? 1,
        newCap: intervention.capacity_vph,
      },
    ]),
  );
}

export function applyToggle(draft, key, oneway) {
  const next = { ...draft };
  if (key === 'forward') {
    const forwardWasClosed = !!(next.forward || next.both);
    const backwardWasClosed = !oneway && !!(next.backward || next.both);
    next.forward = !forwardWasClosed;
    next.backward = backwardWasClosed;
    next.both = false;
  } else if (key === 'backward') {
    const forwardWasClosed = !!(next.forward || next.both);
    const backwardWasClosed = !!(next.backward || next.both);
    next.forward = forwardWasClosed;
    next.backward = !backwardWasClosed;
    next.both = false;
  } else if (key === 'both') {
    next.both = !next.both;
    next.forward = false;
    next.backward = false;
  } else if (key === 'reverse') {
    if (!oneway) {
      next.reverse = false;
    } else {
      next.reverse = !next.reverse;
    }
  }
  if (!oneway && next.forward && next.backward) {
    next.both = true;
    next.forward = false;
    next.backward = false;
  }
  if (oneway) next.backward = false;
  if (!oneway) next.reverse = false;
  return next;
}

export function countInterventions(interventions, newRoads, edgeMeta = {}) {
  const active = Object.entries(interventions || {}).filter(([uid, a]) => isActiveIntervention(a, edgeMeta[uid]?.capacity_vph));
  return active.length + (newRoads || []).length;
}

export function validateNewRoad(origin, destination, lanes, speed) {
  const errors = [];
  if (!origin || !destination) errors.push('Seleziona origine e destinazione');
  if (origin && destination && origin === destination) errors.push('Origine e destinazione coincidono');
  if (lanes < 1 || lanes > 8) errors.push('Corsie tra 1 e 8');
  if (speed < 5 || speed > 140) errors.push('Velocità tra 5 e 140 km/h');
  return errors;
}
